import React, { ComponentProps, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import HistoryLog from '../HistoryLog';
import { EVENT_TIMELINE_ENTITY_TYPES, EVENT_TIMELINE_SUB_TYPES } from 'App/babel/enum/timeline';
import { getTimelineRow } from '../../../babel/components/Helpers/getTimelineRow';
import moment from 'moment';
import T from 'Components/Helpers/translate';
import usePartialLoader, { FetcherFunction } from 'App/components/hooks/usePartialLoader';
import ComparisonTypes from 'Resources/ComparisonTypes';
import TimelineGenericRender, {
	TimelineComponentProps,
	TimelineConfig
} from 'Components/TimelineRow/TimelineGeneric/TimelineCardConfigs';
import Client from 'App/resources/Model/Client';
import type { HistoryLogRenderedBy } from 'App/components/HistoryLog/ModalHistoryLog';
import Event from 'App/resources/Model/Event';
import TimelineLoadMore from 'Components/TimelineRow/TimelineLoadMore/TimelineLoadMore';
import RequestBuilder from 'Resources/RequestBuilder';
import { makeCancelable } from 'Helpers/promise';
import HistoryLogFilterButtons from '../HistoryLogFilterButtons';
import NotesFilterButton from 'App/components/NotesFilter/NotesFilterButton';
import News from 'Resources/News';
import logError from 'Helpers/logError';
import HistoryLogSearch from '../HistoryLogSearch';
import { expandHistoryLogSearch } from '../historyLogSearchSynonyms';

// Filter types that map to entity types for the API
const historyTypes = [
	'all',
	'activities',
	'order',
	'appointments',
	'mail',
	'market',
	'opportunities',
	'signals',
	'news'
] as const;
type HistoryType = (typeof historyTypes)[number];
type HistoryTypeCounts = { [k in HistoryType]?: number };

// Entity type mappings for each filter
const filterEntityTypes: Record<Exclude<HistoryType, 'all' | 'market' | 'news'>, string[]> = {
	activities: ['Activity'],
	order: ['Order', 'Agreement'],
	appointments: ['Appointment'],
	mail: ['Mail'],
	opportunities: ['Opportunity'],
	signals: ['News', 'Signals']
};

const listeners = [
	'activity.added',
	'activity.deleted',
	'activity.updated',
	'appointment.added',
	'appointment.deleted',
	'appointment.updated',
	'comment.added',
	'comment.deleted',
	'comment.updated',
	'news.pin',
	'news.unpin'
];

const fetcher: FetcherFunction<
	{
		client: Client;
		selectedFilters?: HistoryType[];
		onlyMetadata?: boolean;
		hideNews?: boolean;
		search?: string;
	},
	Event
> = (filters, { client, selectedFilters = [], onlyMetadata = false, hideNews, search }) => {
	const event = Tools.Event;

	filters.addFilter(event.attr.clientId, ComparisonTypes.Equals, client.id);
	filters.addFilter(
		event.attr.date,
		ComparisonTypes.LessThanEquals,
		moment.tz(Tools.userTimezone ?? 'Europe/Stockholm')
	);

	// Check if 'market' is selected - it uses a special field filter
	const hasMarket = selectedFilters.includes('market');
	const isAllSelected = selectedFilters.length === 0 || selectedFilters.includes('all');

	// If specific filters are selected (not 'all'), build entity type filter
	if (!isAllSelected) {
		// Collect all entity types from selected filters
		const entityTypes: string[] = [];

		for (const filter of selectedFilters) {
			if (filter === 'market') {
				// Market uses a special filter - handled separately
			} else if (filter in filterEntityTypes) {
				entityTypes.push(...filterEntityTypes[filter as keyof typeof filterEntityTypes]);
			}
		}

		if (hasMarket && entityTypes.length === 0) {
			// Only market selected - use feedMarket filter
			filters.addFilter({ field: 'feedMarket' }, ComparisonTypes.Equals, null);
		} else if (hasMarket && entityTypes.length > 0) {
			// Market + other filters - use OR builder to combine feedMarket OR specific entity types
			const orBuilder = filters.orBuilder();
			orBuilder.next();
			orBuilder.addFilter({ field: 'feedMarket' }, ComparisonTypes.Equals, null);
			orBuilder.next();
			orBuilder.addFilter(event.attr.feed, ComparisonTypes.Equals, null);
			orBuilder.addFilter(event.attr.entityType, ComparisonTypes.Equals, entityTypes);
			orBuilder.done();
		} else if (entityTypes.length > 0) {
			// Only entity type filters (no market)
			// When filtering by specific entity types, we only need the Equals filter
			// The entityTypes array already excludes 'Client' since it's not in filterEntityTypes
			filters.addFilter(event.attr.feed, ComparisonTypes.Equals, null);
			filters.addFilter(event.attr.entityType, ComparisonTypes.Equals, entityTypes);
		}
	} else {
		// "All" selected - exclude feed items and Client entity type
		filters.addFilter(event.attr.feed, ComparisonTypes.Equals, null);
		filters.addFilter(event.attr.entityType, ComparisonTypes.NotEquals, ['Client']);
	}

	if (hideNews) {
		filters.addFilter(event.attr.entityType, ComparisonTypes.NotEquals, ['News']);
	}

	if (search) {
		filters.addFilter(event.attr.search, ComparisonTypes.Equals, expandHistoryLogSearch(search));
	}

	filters.addSort(event.attr.date, false);
	filters.addSort(event.attr.entityType, false);

	if (onlyMetadata) {
		filters.limit = 0;
	}

	return event.find(filters.build());
};

// Fetcher for single history type (used for metadata counts)
const singleTypeFetcher: FetcherFunction<
	{ client: Client; historyType: HistoryType; onlyMetadata?: boolean },
	Event
> = (filters, { client, historyType, onlyMetadata = false }) => {
	const event = Tools.Event;

	filters.addFilter(event.attr.clientId, ComparisonTypes.Equals, client.id);
	filters.addFilter(
		event.attr.date,
		ComparisonTypes.LessThanEquals,
		moment.tz(Tools.userTimezone ?? 'Europe/Stockholm')
	);

	if (historyType !== 'market') {
		filters.addFilter(event.attr.feed, ComparisonTypes.Equals, null);
		filters.addFilter(event.attr.entityType, ComparisonTypes.NotEquals, ['Client']);
	}

	if (historyType === 'market') {
		filters.addFilter({ field: 'feedMarket' }, ComparisonTypes.Equals, null);
	} else if (historyType in filterEntityTypes) {
		filters.addFilter(
			event.attr.entityType,
			ComparisonTypes.Equals,
			filterEntityTypes[historyType as keyof typeof filterEntityTypes]
		);
	}

	filters.addSort(event.attr.date, false);

	if (onlyMetadata) {
		filters.limit = 0;
	}

	return event.find(filters.build());
};

const getNews: FetcherFunction<{ client: Client }, Event> = async (filter, { client }) => {
	try {
		if (client.prospectingId) {
			const result = await News.getClientNews(client.id);
			return {
				metadata: result.metadata,
				data: result.data.map(
					entry =>
						({
							...entry,
							entityType: 'News',
							entityId: entry.id,
							clientId: client.id,
							news: entry,
							users: []
						}) as Event
				)
			};
		} else {
			return { data: [], metadata: { total: 0 } };
		}
	} catch (err) {
		logError(err, 'Could not get client news');
		return { data: [], metadata: { total: 0 } };
	}
};

type Props = {
	client: Client;
	notesFilterActive?: boolean;
	renderedBy: HistoryLogRenderedBy;
	cardAdditionalProps?: Partial<TimelineComponentProps>;
} & Partial<ComponentProps<typeof HistoryLog>>;

const ClientHistoryLog = ({ client, notesFilterActive = true, renderedBy, cardAdditionalProps, ...props }: Props) => {
	const [selectedFilters, setSelectedFilters] = useState<HistoryType[]>(['all']);
	const [metadata, setMetadata] = useState<HistoryTypeCounts>({ all: 0 });
	const [search, setSearch] = useState('');
	const onSearchChange = useCallback((value: string) => setSearch(value), []);

	// Determine if we should use the news fetcher
	const isNewsOnly = selectedFilters.length === 1 && selectedFilters[0] === 'news';

	// Create a stable key for the fetcher props to trigger resets
	// Sort to ensure consistent key regardless of selection order
	const filterKey = selectedFilters.slice().sort().join(',');

	const {
		data: events,
		total: totalEvents,
		loading,
		loadMore,
		reset
	} = usePartialLoader({
		fetcher: isNewsOnly ? getNews : fetcher,
		broadcastTypes: listeners,
		fetcherProps: {
			client,
			selectedFilters,
			hideNews: renderedBy.type !== 'company',
			search
		}
	});

	const containerRef = useRef<HTMLDivElement>(null);

	// Latch search visibility so clearing the query doesn't unmount the input
	// (which would steal focus) while the timeline refetches.
	const hasShownSearchRef = useRef(false);
	if (totalEvents > 0 || search) {
		hasShownSearchRef.current = true;
	}
	const showSearchInput = hasShownSearchRef.current;

	const filterButtons = [
		{ title: T('all'), value: 'all' as const },
		{ title: T('default.activities'), value: 'activities' as const, icon: 'activity' as const },
		{ title: T('default.order'), value: 'order' as const, icon: 'dollar' as const },
		{ title: T('default.appointments'), value: 'appointments' as const, icon: 'calendar' as const },
		{ title: T('default.mail'), value: 'mail' as const, icon: 'envelope' as const },
		{ title: T('default.market'), value: 'market' as const, icon: 'bullhorn' as const },
		{ title: T('default.opportunities'), value: 'opportunities' as const, icon: 'opportunity' as const },
		{ title: T('default.signals'), value: 'signals' as const, icon: 'signal' as const }
	];

	const handleFilterChange = (value: HistoryType) => {
		setSelectedFilters(prev => {
			// If clicking 'all', reset to only 'all'
			if (value === 'all') {
				return ['all'];
			}

			// If 'all' was previously selected, replace with just the clicked filter
			if (prev.includes('all')) {
				return [value];
			}

			// Toggle the filter
			if (prev.includes(value)) {
				const newFilters = prev.filter(f => f !== value);
				// If nothing selected, default back to 'all'
				return newFilters.length === 0 ? ['all'] : newFilters;
			} else {
				return [...prev, value];
			}
		});
	};

	const getClientTimeline = (event: Event, isLastElem: boolean = false) => {
		const entityType = event.entityType || '';
		const subType = event.subType || '';

		if (entityType === EVENT_TIMELINE_ENTITY_TYPES.CONTACT) {
			switch (subType.toLowerCase()) {
				case EVENT_TIMELINE_SUB_TYPES.CREATED:
					return (
						<TimelineGenericRender
							type={TimelineConfig.AddedContact}
							event={event}
							includeIcon
							hideLine={isLastElem}
							{...cardAdditionalProps}
						/>
					);
				default:
					return null;
			}
		}

		return getTimelineRow(event, 'Client', notesFilterActive, isLastElem, renderedBy, cardAdditionalProps);
	};

	useEffect(() => {
		if (renderedBy.type !== 'company') {
			return;
		}
		const promise = makeCancelable(
			Promise.all(
				historyTypes.map(async type => {
					if (type === 'news') {
						return getNews(new RequestBuilder(), { client }).then(res => {
							return { type, count: res.metadata.total };
						});
					}

					return singleTypeFetcher(new RequestBuilder(), { client, historyType: type, onlyMetadata: true }).then(
						res => {
							return { type, count: res.metadata.total };
						}
					);
				})
			).then(res => {
				const newMetadata = res.reduce(
					(acc, cur) => {
						acc[cur.type] = cur.count;
						return acc;
					},
					{} as Record<HistoryType, number>
				);
				setMetadata(newMetadata);
			})
		);

		return () => {
			promise.cancel();
		};
	}, []);

	const isInitialMount = useRef(true);
	useEffect(() => {
		if (isInitialMount.current) {
			isInitialMount.current = false;
			return;
		}
		reset();
	}, [filterKey, search]);

	if (!client || !client.id) {
		return null;
	}

	let timelineElements: ReactNode[] = [];
	if (events && events.length) {
		timelineElements = events.map(event => {
			return event ? <div key={event.id}>{getClientTimeline(event)}</div> : null;
		});
	}

	const clientRegEvent = {
		entityType: EVENT_TIMELINE_ENTITY_TYPES.CLIENT,
		subType: EVENT_TIMELINE_SUB_TYPES.CREATED,
		entityId: client.id,
		users: client.regBy ? [client.regBy] : [],
		date: client.regDate
	};

	return (
		<div className="HistoryLog" ref={containerRef}>
			<HistoryLog
				title={T('history')}
				loading={loading}
				{...props}
				searchInput={showSearchInput ? <HistoryLogSearch onSearchChange={onSearchChange} /> : null}
				selectButtons={
					renderedBy.type === 'company' ? (
						<>
							<NotesFilterButton />
							<HistoryLogFilterButtons
								buttons={filterButtons}
								containerRef={containerRef}
								metadata={metadata}
								selectedFilters={selectedFilters}
								onFilterChange={handleFilterChange}
								multiSelect
							/>
							<HistoryLogFilterButtons
								buttons={[{ title: T('default.news'), value: 'news' as const, icon: 'newspaper' as const }]}
								containerRef={containerRef}
								metadata={metadata}
								selectedFilters={selectedFilters}
								onFilterChange={handleFilterChange}
								multiSelect
							/>
						</>
					) : (
						props.selectButtons
					)
				}
			>
				{timelineElements}
				{timelineElements.length < totalEvents ? (
					<TimelineLoadMore
						newEventsLoading={loading}
						numEventsLoaded={events.length}
						totalEvents={totalEvents ? totalEvents : null}
						incrementOffset={loadMore}
					/>
				) : null}
				<TimelineGenericRender
					type={TimelineConfig.AddedClient}
					event={clientRegEvent}
					includeIcon
					hideLine
					{...cardAdditionalProps}
				/>
			</HistoryLog>
		</div>
	);
};

export default ClientHistoryLog;
